import { ApiError } from '@/shared/services/baseApiClient';
import { appsApi } from '@/features/apps/shared/services/apps';
import { InstallAppViewModel } from '@/features/apps/shared/models';
import { webAssistantApi } from './webAssistant';
import { WebAssistantViewModel } from '../models/webAssistantViewModel';
import { InstallWebAssistantViewModel } from '../models/request/installWebAssistantViewModel';
import { InstalledAppViewModel } from '../models/response/installedAppViewModel';

class WebAssistantAppService {
  // installs the 'WebAssistant' domain app
  // and the 'Apps' domain app if it doesn't exist already
  public fullInstall(
    workspaceId: string,
    workspaceName: string,
    defaultViewModel: WebAssistantViewModel,
  ): Promise<InstalledAppViewModel> {
    const installedAppId$ = this.getInstalledAppId(workspaceId, 'webassistant');

    const identityVerificationSecret$ = this.generateIdentityVerificationSecret();

    return Promise.all([installedAppId$, identityVerificationSecret$]).then((values) => {
      const installedAppId = values[0];
      const identityVerificationSecret = values[1];
      return this.configureWebAssistant(
        workspaceId,
        workspaceName,
        installedAppId,
        identityVerificationSecret,
        defaultViewModel,
      );
    });
  }

  private configureWebAssistant(
    workspaceId: string,
    workspaceName: string,
    installedAppId: string,
    identityVerificationSecret: string,
    defaultViewModel: WebAssistantViewModel,
  ) {
    const config = defaultViewModel as InstallWebAssistantViewModel;
    config.installId = installedAppId;
    config.workspaceId = workspaceId;
    config.identityVerificationSecret = identityVerificationSecret;
    return webAssistantApi.installWebAssistant(config);
  }

  private generateIdentityVerificationSecret() {
    const array = new Int32Array(32);
    crypto.getRandomValues(array);
    return crypto.subtle.digest('SHA-256', array).then((hash) => {
      return this.toBase64Url(hash);
    });
  }

  private toBase64Url(buffer: any) {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    const encoded = window.btoa(binary);
    return encoded
      .replace(/=+$/, '') // trim =
      .replace(/\+/g, '-') // replace +
      .replace(/\//g, '_'); // replace /
  }

  /** Gets the installed app, or creates it if it doesn't exist */
  private getInstalledAppId(workspaceId: string, appId: string): Promise<string> {
    return appsApi
      .getInstalledApp(workspaceId, appId)
      .then((app) => {
        // app exists, return its id
        return app.installedAppId;
      })
      .catch((error: ApiError) => {
        if (error.status === 404) {
          // app does not exist, we'll create it
          return this.installInstalledApp(workspaceId, appId).then((app) => {
            return app.installedAppId;
          });
        }
        // anything other than a 404 is unexpected
        throw new Error(`Encountered unexpected error: Http Status ${error.status}`);
      });
  }

  private installInstalledApp(workspaceId: string, appId: string) {
    const installRequest = { appId, published: true } as InstallAppViewModel;
    return appsApi.install(workspaceId, installRequest);
  }
}

export const webAssistantAppService = new WebAssistantAppService();
